<script setup lang="ts">
import { IS_NATIVE } from '~/utils/platform'

const { floor, checking, retry } = useNativeUpdate()
// The logo doubles as the home button on every page except the menu.
// On the NODE screen (v2.48, per Javi) the chrome moves INTO the frozen
// ribbon — no floating logo or chip cluster over the forest.
const route = useRoute()
const { visible: identityNudgeOpen } = useNativeIdentityNudge()
const { t } = useLocale()
const isPolicyPage = computed(() => ['/privacy', '/terms', '/support', '/delete-account'].includes(route.path.replace(/\/$/, '')))
// Screens with a stone banner host the chrome INSIDE it (v2.48 node,
// v3.37 map) — no floating logo or chip cluster over the artwork.
const inNode = computed(() => route.path === '/encounter' || route.path === '/map')
const showLogo = computed(() => !isPolicyPage.value && route.path !== '/' && route.path !== '/encounter')
</script>

<template>
  <UpdateRequired v-if="IS_NATIVE && floor && !isPolicyPage" :checking="checking" @retry="retry" />
  <div v-else>
    <div :inert="identityNudgeOpen && !isPolicyPage">
      <!-- v6.23: logo.webp is Javi's logo.png re-encoded at the same 720×615
           (q90, indistinguishable at native pixels) — 454 KB → 65 KB on
           every first load. -->
      <NuxtLink v-if="showLogo" to="/" class="app-logo-link" :title="t('app.homeTip')">
        <img src="/logo.webp?v=4" alt="Elemental Dispatch — home" class="app-logo">
      </NuxtLink>
      <div v-if="!inNode && !isPolicyPage" class="top-chips">
        <GameMenu />
        <SessionClock />
      </div>
      <NuxtPage />
      <RunSyncChooser v-if="!isPolicyPage" />
      <AliasPicker v-if="!isPolicyPage" />
    </div>
    <NativeIdentityNudge v-if="IS_NATIVE && !isPolicyPage" />
  </div>
  <BackExitHint />
</template>

<style scoped>
/* 🔊⏱ Top-right cluster (v1.70): mute chip beside the clock, every
 * screen. The clock's own fixed positioning is neutralized here. */
.top-chips {
  position: fixed;
  /* 📱 M6: clear of the status bar, the notch and a landscape cutout. */
  top: calc(8px + var(--safe-top));
  right: calc(10px + var(--safe-right));
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 6px;
}
.top-chips :deep(.session-clock) {
  position: static;
}
.app-logo-link {
  position: fixed;
  top: calc(4px + var(--safe-top));
  left: calc(10px + var(--safe-left));
  /* Below the frozen headers (z 30): when a stuck bar scrolls over, it
   * covers the logo cleanly instead of the logo punching through (v0.64). */
  z-index: 20;
  line-height: 0;
}
.app-logo {
  height: 42px;
  width: auto;
  filter: drop-shadow(0 2px 4px rgba(31, 58, 95, 0.35));
  transition: transform 0.12s ease;
}
.app-logo-link:hover .app-logo {
  transform: scale(1.06);
}
</style>
